import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { matchIsValidTel,MuiTelInput } from 'mui-tel-input';

import { type PatientAddress } from '../../types/types';

import { US_STATES } from './patientValidation';

interface AddressCardProps {
  address: PatientAddress | null | undefined;
  onChange: (address: PatientAddress | null) => void;
  onBlur?: () => void;
  title?: string;
  showDelete?: boolean;
  onDelete?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
}

const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onChange,
  onBlur,
  title = 'Address',
  showDelete = false,
  onDelete,
  errors = {},
  highlightedFields = [],
  disabled = false,
}) => {
  const handleFieldChange = (field: keyof PatientAddress, value: string) => {
    const updatedAddress: PatientAddress = {
      ...(address || {}),
      [field]: value || null,
    };
    onChange(updatedAddress);
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    handleFieldChange('state', event.target.value);
  };

  const handlePhoneChange = (newValue: string) => {
    // Convert to E.164 format if valid
    if (matchIsValidTel(newValue)) {
      // Extract just the digits and country code
      const cleaned = newValue.replace(/\D/g, '');
      const e164 = cleaned.startsWith('1') ? `+${cleaned}` : `+1${cleaned}`;
      handleFieldChange('phone', e164);
    } else {
      handleFieldChange('phone', newValue);
    }
  };

  const formatPhoneDisplay = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // If it's already in E.164, mui-tel-input will format it nicely
    return phone;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {showDelete && onDelete && (
          <IconButton size="small" onClick={onDelete} color="error" disabled={disabled}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Address Line 1"
            value={address?.line1 || ''}
            onChange={(e) => handleFieldChange('line1', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.line1}
            helperText={errors.line1}
            sx={{
              ...(highlightedFields.includes('line1') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Address Line 2 (Apt, Suite, etc.)"
            value={address?.line2 || ''}
            onChange={(e) => handleFieldChange('line2', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.line2}
            helperText={errors.line2}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="City"
            value={address?.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.city}
            helperText={errors.city}
            sx={{
              ...(highlightedFields.includes('city') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth size="small" disabled={disabled} error={!!errors.state}>
            <InputLabel>State</InputLabel>
            <Select
              value={address?.state || ''}
              onChange={handleStateChange}
              onBlur={onBlur}
              label="State"
              sx={{
                ...(highlightedFields.includes('state') && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {US_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="ZIP Code"
            value={address?.zip || ''}
            onChange={(e) => {
              // Allow only digits and hyphen, format as XXXXX or XXXXX-XXXX
              const value = e.target.value.replace(/[^\d-]/g, '');
              if (value.length <= 10) {
                handleFieldChange('zip', value);
              }
            }}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.zip}
            helperText={errors.zip || 'Format: 12345 or 12345-6789'}
            placeholder="12345"
            sx={{
              ...(highlightedFields.includes('zip') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <MuiTelInput
            label="Phone (Optional)"
            value={formatPhoneDisplay(address?.phone)}
            onChange={handlePhoneChange}
            onBlur={onBlur}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            disabled={disabled}
            error={!!errors.phone}
            helperText={errors.phone || 'Location-specific phone (optional)'}
            sx={{
              width: '100%',
              '& input': {
                height: '1.4375em',
              },
              ...(highlightedFields.includes('phone') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddressCard;